import { Controller } from "@hotwired/stimulus";

// Lightbox: full-screen photo viewer over a native <dialog>. Any element can
// open it at a given photo (openAt with an index param); arrows and buttons
// step through the photos with wrap-around. Escape closes via the dialog's
// native cancel behavior.
//
//   data-controller="lightbox"
//   data-lightbox-photos-value='[{"src":"…","alt":"…","caption":"…"}, …]'
export default class extends Controller {
  static targets = ["dialog", "image", "counter", "caption"];
  static values = { photos: Array, index: { type: Number, default: 0 } };

  disconnect() {
    this.close();
  }

  openAt(event) {
    this.indexValue = event.params.index ?? 0;
    this.dialogTarget.showModal();
    // The page behind the dialog keeps its scrollbar otherwise.
    document.body.classList.add("overflow-hidden");
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close();
  }

  // Wired to the dialog's `close` event, so Escape is covered too.
  released() {
    document.body.classList.remove("overflow-hidden");
  }

  next() {
    this.indexValue = (this.indexValue + 1) % this.photosValue.length;
  }

  previous() {
    this.indexValue =
      (this.indexValue - 1 + this.photosValue.length) % this.photosValue.length;
  }

  navigate(event) {
    if (event.key === "ArrowRight") this.next();
    if (event.key === "ArrowLeft") this.previous();
  }

  // Swipe on touch devices: remember where the finger landed…
  touchStart(event) {
    const touch = event.changedTouches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  // …and step through the gallery when it lifts after a mostly-horizontal drag.
  touchEnd(event) {
    if (this.touchStartX == null) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    this.touchStartX = null;

    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
    deltaX < 0 ? this.next() : this.previous();
  }

  // Clicks on the dialog's empty areas (not the photo or the controls) close it.
  dismissOnClickOutside(event) {
    // The caption bar is exempt so selecting the text doesn't dismiss.
    if (event.target.closest("img, button, [data-lightbox-bar]")) return;
    this.close();
  }

  indexValueChanged() {
    const photo = this.photosValue[this.indexValue];
    if (!photo || !this.hasImageTarget) return;

    this.imageTarget.src = photo.src;
    this.imageTarget.alt = photo.alt;
    this.counterTarget.textContent = `${this.indexValue + 1} / ${this.photosValue.length}`;
    // Photos without a caption collapse the figcaption rather than leave a gap.
    if (this.hasCaptionTarget) {
      this.captionTarget.textContent = photo.caption ?? "";
      this.captionTarget.classList.toggle("hidden", !photo.caption);
    }
    this.preloadNeighbors();
  }

  preloadNeighbors() {
    const count = this.photosValue.length;
    [this.indexValue + 1, this.indexValue - 1 + count].forEach((neighbor) => {
      new Image().src = this.photosValue[neighbor % count].src;
    });
  }
}
